import React, { useEffect, useState } from "react";
import Layout from "../components/Layout";
import { api, formatBRL } from "../lib/api";
import { useAuth } from "../lib/auth";
import {
  Wallet, TrendingUp, Coins, Banknote, Filter,
  CheckCircle2, RefreshCcw, AlertTriangle, Users, Link2, Briefcase, Layers, FileText,
} from "lucide-react";

const PERIODS = [
  { v: "", label: "Tudo", tid: "todos" },
  { v: "today", label: "Hoje", tid: "hoje" },
  { v: "week", label: "Semana", tid: "semana" },
  { v: "mes_atual", label: "Mês atual", tid: "mes_atual" },
  { v: "mes_anterior", label: "Mês anterior", tid: "mes_anterior" },
  { v: "last_30", label: "Últimos 30d", tid: "last_30" },
  { v: "custom", label: "Personalizado", tid: "custom" },
];

function Kpi({ label, value, icon: Icon, accent, testid, detail }) {
  return (
    <div className="fc-card p-5 fc-card-hover" data-testid={testid}>
      <div className="flex items-start justify-between">
        <div className="text-[11px] uppercase tracking-wider text-[#A3A3A3] font-medium">{label}</div>
        {Icon && <div className={`w-8 h-8 rounded-md flex items-center justify-center border ${accent || "border-[#262626] text-[#A3A3A3]"}`}><Icon size={15} strokeWidth={1.8}/></div>}
      </div>
      <div className="fc-kpi-value text-2xl sm:text-[26px] mt-3 leading-none">{value}</div>
      {detail && (
        <div className="mt-3 pt-2 border-t border-[#1f1f1f] space-y-1 text-[11px]" data-testid={`${testid}-detail`}>
          {detail}
        </div>
      )}
    </div>
  );
}

export default function FinancialDashboardPage() {
  const { user } = useAuth();
  const [period, setPeriod] = useState("");
  const [start, setStart] = useState("");
  const [end, setEnd] = useState("");
  const [d, setD] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const params = {};
      if (period) params.period = period;
      if (period === "custom") { if (start) params.start = start; if (end) params.end = end; }
      const { data } = await api.get("/dashboard/financial", { params });
      setD(data);
    } finally { setLoading(false); }
  };

  useEffect(() => {
    if (period !== "custom" || (start && end)) { const t = setTimeout(load, 200); return () => clearTimeout(t); }
    // eslint-disable-next-line
  }, [period, start, end]);

  const isSocio = user?.role === "socio";

  return (
    <Layout>
      <div className="mb-7">
        <div className="text-[11px] uppercase tracking-[0.2em] text-[#D4AF37]">{isSocio ? "Sócio" : "Executivo"}</div>
        <h1 className="font-display text-3xl font-bold tracking-tight mt-1">{isSocio ? "Meu dashboard" : "Dashboard financeiro"}</h1>
        <p className="text-[#A3A3A3] text-sm mt-1">{isSocio ? "Resultados do seu capital" : "Visão executiva da operação"}</p>
      </div>

      <div className="fc-card p-4 mb-6 flex flex-col sm:flex-row sm:items-center gap-3 flex-wrap">
        <div className="flex items-center gap-2 text-[#A3A3A3] text-sm"><Filter size={14}/> Período:</div>
        <div className="flex flex-wrap gap-2">
          {PERIODS.map(p => (
            <button key={p.v} onClick={()=>setPeriod(p.v)} className={`px-3.5 py-1.5 rounded-md text-[12.5px] border transition-colors ${period === p.v ? "bg-[#D4AF37] text-black border-[#D4AF37]" : "border-[#262626] text-[#A3A3A3] hover:border-[#3a3a3a]"}`} data-testid={`period-${p.tid}`}>{p.label}</button>
          ))}
        </div>
        {period === "custom" && (
          <div className="flex items-center gap-2 ml-auto">
            <input type="date" className="fc-input text-sm" value={start} onChange={e=>setStart(e.target.value)}/>
            <span className="text-[#737373]">→</span>
            <input type="date" className="fc-input text-sm" value={end} onChange={e=>setEnd(e.target.value)}/>
          </div>
        )}
      </div>

      {loading ? (
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">{[1,2,3,4,5,6,7,8].map(i=>(<div key={i} className="fc-card h-28 shimmer"/>))}</div>
      ) : d && (
        <>
          {(d.start || d.end) && (
            <div className="mb-4 text-[11.5px] text-[#A3A3A3] flex items-center gap-2" data-testid="active-period-label">
              <Filter size={12} className="text-[#D4AF37]"/>
              <span>
                Filtrado por período:{" "}
                <span className="font-mono-tab text-[#D4AF37]">{(d.start || "").slice(0,10) || "—"}</span>
                {" "}até{" "}
                <span className="font-mono-tab text-[#D4AF37]">{(d.end || "").slice(0,10) || "—"}</span>
              </span>
            </div>
          )}
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
            <Kpi
              label="Capital na rua"
              value={formatBRL(d.capital_na_rua)}
              icon={Wallet}
              accent="border-[#D4AF37]/40 text-[#D4AF37]"
              testid="kpi-capital-na-rua"
              detail={(d.capital_na_rua_recuperacao ?? 0) > 0 && (
                <>
                  <div className="flex justify-between" data-testid="kpi-capital-ativo">
                    <span className="text-[#A3A3A3]">Ativo</span>
                    <span className="font-mono text-emerald-300">{formatBRL(d.capital_na_rua_ativo)}</span>
                  </div>
                  <div className="flex justify-between" data-testid="kpi-capital-recuperacao">
                    <span className="text-[#A3A3A3]">Recuperação</span>
                    <span className="font-mono text-amber-300">{formatBRL(d.capital_na_rua_recuperacao)}</span>
                  </div>
                </>
              )}
            />
            <Kpi label="Juros previstos" value={formatBRL(d.juros_previstos)} icon={TrendingUp} accent="border-[#D4AF37]/40 text-[#D4AF37]" testid="kpi-juros-previstos"/>
            <Kpi label="Total a receber" value={formatBRL(d.total_a_receber)} icon={Coins} accent="border-emerald-500/30 text-emerald-300" testid="kpi-total-receber"/>
            <Kpi label="Juros recebidos no período" value={formatBRL(d.juros_recebidos_periodo)} icon={Banknote} accent="border-emerald-500/30 text-emerald-300" testid="kpi-juros-recebidos"/>
            <Kpi label="Juros pendentes" value={formatBRL(d.juros_pendentes)} icon={AlertTriangle} accent="border-yellow-500/30 text-yellow-300" testid="kpi-juros-pendentes"/>
            <Kpi label="Capital recuperado no período" value={formatBRL(d.capital_recuperado_periodo)} icon={Wallet} accent="border-emerald-500/30 text-emerald-300" testid="kpi-capital-recuperado"/>
            <Kpi label="Lucro do período (empresa)" value={formatBRL(d.juros_recebidos_empresa)} icon={TrendingUp} accent="border-[#D4AF37]/40 text-[#D4AF37]" testid="kpi-lucro-empresa"/>
            <Kpi label="Contratos em atraso" value={d.contratos_atrasados} icon={FileText} accent="border-red-500/30 text-red-300" testid="kpi-contratos-atrasados"/>
          </div>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-4">
            <Kpi label="Contratos ativos" value={d.contratos_ativos} icon={CheckCircle2} accent="border-[#D4AF37]/40 text-[#D4AF37]" testid="kpi-contratos-ativos"/>
            <Kpi label="Clientes em atraso" value={d.clientes_atrasados ?? 0} icon={Users} accent="border-red-500/30 text-red-300" testid="kpi-clientes-atrasados"/>
            <Kpi label="Parcelas em atraso" value={d.parcelas_atrasadas ?? 0} icon={Layers} accent="border-red-500/30 text-red-300" testid="kpi-parcelas-atrasadas"/>
            <Kpi label="Contratos quitados" value={d.contratos_quitados} icon={CheckCircle2}/>
            <Kpi label="Contratos renovados" value={d.contratos_renovados} icon={RefreshCcw}/>
          </div>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
            <Kpi label="Clientes ativos" value={d.clientes_ativos} icon={Users}/>
            {!isSocio && <Kpi label="Indicadores ativos" value={d.indicadores_ativos} icon={Link2}/>}
            {!isSocio && <Kpi label="Sócios ativos" value={d.socios_ativos} icon={Briefcase}/>}
          </div>
        </>
      )}
    </Layout>
  );
}
